import React, { useEffect, useRef, useState } from 'react';

import { Flexbox, Text } from '@schoolinks/ui-components';

import cn from 'classnames';
import { noop } from 'lodash';
import { bool, func, number, oneOf, string } from 'prop-types';
import ReactPlayer from 'react-player';

import ReportBlockedVideo from 'components/Common/ReportBlockedVideo';
import VideoPlayerButtonIcon from 'components/Common/VideoPlayerButtonIcon';
import Api from 'core/api';
import useMediaInfo from 'hooks/useMediaInfo';
import useMutation from 'hooks/useMutation';

import s from './VideoPlayer.css';

/**
 * Handles playing videos
 *
 * @param {bool} allowDownload - if true, native player controls will show download button
 * @param {string} buttonSize - size of play button (small is typical for lists, large is typical for standalone usages)
 * @param {string} containerClass - used to style the container
 * @param {string} errorMessage - custom error message if video is unable to be played
 * @param {string} height - height of video element
 * @param {boolean} loop - sets the video to loop
 * @param {func} onDuration - callback containing duration of video
 * @param {func} onError - callback fired when video encounters error on load
 * @param {func} onEnded - callback fired when video ends
 * @param {func} onPlay - callback fired when video starts or resumes playing
 * @param {func} onPause - callback fired when video is paused
 * @param {func} onStart - callback fired when video first starts
 * @param {boolean} playing - plays or pauses video
 * @param {boolean} showControlsOnLoad - display default video element controls before video starts
 * @param {boolean} showReportBlocked - displays the component used to report a blocked video
 * @param {string} textOverlay - optional text to place on top of video
 * @param {boolean} trackMentorVideo - sends view statistics for mentor videos
 * @param {string} url - url of the video to be played
 * @param {number} videoId - id of video used when tracking views
 * @param {string} width - width of video element
 * @param {boolean} playsinline
 * @param {string} videoContainerClassName - additional styling for video container class
 * @param {bool} hidePlayButton - If true, hides the play button
 * @param {func} onProgress - Callback containing played and loaded progress as a fraction, and playedSeconds and loadedSeconds in seconds
 * @param {string} transcription - Link for the transcription file
 */
const VideoPlayer = ({
  allowDownload,
  buttonSize,
  containerClass,
  errorMessage,
  height,
  loop,
  onDuration,
  onError,
  onEnded,
  onPlay,
  onPause,
  onStart,
  playing,
  showControlsOnLoad,
  showReportBlocked,
  textOverlay,
  trackMentorVideo,
  url,
  videoId,
  width,
  playsinline,
  videoContainerClassName,
  hidePlayButton,
  onProgress,
  transcription,
}) => {
  const playerRef = useRef();
  const isSmall = buttonSize === 'small';
  const isLarge = buttonSize === 'large';
  const { isMobile } = useMediaInfo();

  const [error, setError] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [showControls, setShowControls] = useState(showControlsOnLoad);
  const [showTextOverlay, setShowTextOverlay] = useState(Boolean(textOverlay));

  const { mutate: viewVideo } = useMutation({
    apiFunc: Api.videoQna.viewVideo,
    onError: e => console.warn('Could not mark mentor video viewed', e),
  });

  const handleEnd = () => {
    setShowPlayButton(true);
    setHasStarted(false);
    playerRef.current?.seekTo(0);
    onEnded();
  };

  const handleError = err => {
    console.error(err, 'error while playing the video');
    setError(errorMessage);
    onError(err);
  };

  const handleReady = () => {
    if (playing) {
      setHasStarted(true);
      setShowPlayButton(false);
    }
  };

  const handleHideControls = () => {
    setShowControls(false);
  };

  const handlePause = () => {
    setShowTextOverlay(Boolean(textOverlay));
    onPause();
  };

  const handlePlay = () => {
    setShowTextOverlay(false);
    setShowPlayButton(false);
    onPlay();
    if (ReactPlayer.canPlay(url) && error) {
      setError(null);
    }
  };

  const handleShowControls = () => {
    setShowControls(true);
  };

  const handleStartPlayer = () => {
    setShowPlayButton(false);
    setHasStarted(true);
  };

  const handleStart = () => {
    if (trackMentorVideo) {
      if (!videoId) {
        console.warn('Pass in videoId to track views');
      }
      viewVideo({ qnaVideo: videoId });
    }
    onStart();
  };

  useEffect(() => {
    setError(null);
    setShowPlayButton(true);
  }, [url]);

  useEffect(() => {
    if (!ReactPlayer.canPlay(url)) {
      setError(errorMessage);
    }
  }, [errorMessage, url]);

  return (
    <Flexbox className={containerClass} flexDirection="column">
      <div
        className={cn(s.videoContainer, videoContainerClassName, {
          [s.videoContainerPlaying]: !showPlayButton,
        })}
        onMouseEnter={handleShowControls}
        onMouseLeave={handleHideControls}
      >
        {showPlayButton && !hidePlayButton && (
          <button
            aria-label="Play video"
            className={cn(s.playButton, {
              [s.smallPlayButton]: isSmall,
              [s.largePlayButton]: isLarge,
            })}
            onClick={handleStartPlayer}
            type="button"
            data-testid="videoPlayerPlayButton"
          >
            <VideoPlayerButtonIcon />
          </button>
        )}
        {error && (
          <Text
            className={s.errorMessage}
            color="orangeDark"
            component="h3"
            variant='titleSm700'
          >
            {error}
          </Text>
        )}
        {showTextOverlay && (
          <Text
            className={s.textOverlay}
            color="white"
            component="h3"
            variant={isMobile || isSmall ? 'titleSmBold' : 'h5'}
          >
            {textOverlay}
          </Text>
        )}
        <ReactPlayer
          config={{
            file: {
              attributes: {
                crossOrigin: 'true',
                preload: 'metadata',
                ...(allowDownload ? {} : { controlsList: 'nodownload' }),
              },
              ...(Boolean(transcription) && {
                tracks: [
                  {
                    default: true,
                    kind: 'subtitles',
                    srcLang: 'en',
                    src: transcription,
                    label: 'English',
                    mode: 'showing',
                  },
                ],
              }),
            },
            youtube: {
              playerVars: {
                cc_load_policy: 1, // closed captions TRUE
                rel: 0, // don't show any related videos
                widget_referrer: 'app.schoolinks.com',
              },
            },
          }}
          playsinline={playsinline}
          controls={showControls}
          height={height}
          loop={loop}
          onDuration={onDuration}
          onEnded={handleEnd}
          onError={handleError}
          onPause={handlePause}
          onPlay={handlePlay}
          onStart={handleStart}
          playing={hasStarted}
          ref={playerRef}
          url={url}
          width={width}
          onReady={handleReady}
          onProgress={onProgress}
        />
      </div>
      {showReportBlocked ? <ReportBlockedVideo url={url} /> : null}
    </Flexbox>
  );
};

VideoPlayer.propTypes = {
  allowDownload: bool,
  buttonSize: oneOf(['small', 'large']),
  containerClass: string,
  errorMessage: string,
  height: string,
  loop: bool,
  onDuration: func,
  onError: func,
  onEnded: func,
  onPause: func,
  onPlay: func,
  onStart: func,
  playing: bool,
  showControlsOnLoad: bool,
  showReportBlocked: bool,
  textOverlay: string,
  trackMentorVideo: bool,
  url: string.isRequired,
  videoId: number,
  width: string,
  playsinline: bool,
  videoContainerClassName: string,
  hidePlayButton: bool,
  onProgress: func,
  transcription: string,
};

VideoPlayer.defaultProps = {
  allowDownload: false,
  buttonSize: 'large',
  containerClass: undefined,
  errorMessage: 'Error playing video',
  height: undefined,
  loop: false,
  onDuration: noop,
  onError: noop,
  onEnded: noop,
  onPause: noop,
  onPlay: noop,
  onStart: noop,
  playing: false,
  showControlsOnLoad: true,
  showReportBlocked: false,
  textOverlay: undefined,
  trackMentorVideo: false,
  videoId: undefined,
  width: undefined,
  playsinline: false,
  videoContainerClassName: undefined,
  hidePlayButton: false,
  onProgress: noop,
  transcription: null,
};

export default VideoPlayer;
